import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Link2, BarChart3, Palette, ShoppingBag, ArrowRight, Sparkles, Zap } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/auth');
    }
  };

  const features = [
    {
      icon: Link2,
      title: 'All Your Links',
      description: 'Share everything in one place. Social profiles, websites, and more.',
      className: 'col-span-1 row-span-1'
    },
    {
      icon: ShoppingBag,
      title: 'Sell Products',
      description: 'Add product links with prices and turn followers into customers.',
      className: 'col-span-1 row-span-1'
    },
    {
      icon: BarChart3,
      title: 'Track Clicks',
      description: 'See which links perform best with detailed analytics.',
      className: 'col-span-1 row-span-1'
    },
    {
      icon: Palette,
      title: 'Custom Themes',
      description: 'Match your brand with beautiful color themes.',
      className: 'col-span-1 row-span-1'
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      
      {/* Navbar */}
      <nav className="relative z-10 flex items-center justify-between px-6 py-5 max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-2"
        >
          <div className="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="font-outfit font-bold text-xl text-white">One Link</span>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-4"
        >
          {user ? (
            <Button 
              data-testid="dashboard-btn"
              onClick={() => navigate('/dashboard')}
              className="rounded-full bg-violet-500 hover:bg-violet-600 text-white px-6"
            >
              Dashboard
            </Button>
          ) : (
            <>
              <Button 
                data-testid="login-btn"
                variant="ghost" 
                onClick={() => navigate('/auth')}
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
              >
                Log in
              </Button>
              <Button 
                data-testid="signup-btn"
                onClick={() => navigate('/auth?mode=register')}
                className="rounded-full bg-violet-500 hover:bg-violet-600 text-white px-6 glow-violet"
              >
                Sign up free
              </Button>
            </>
          )}
        </motion.div>
      </nav>

      {/* Hero Section */}
      <main className="relative z-10 px-6 pt-20 pb-32">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-outfit font-black text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[1.1]">
              One Link for
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-violet-500 to-fuchsia-500">
                Everything
              </span>
            </h1>
          </motion.div>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 text-lg text-[#A1A1AA] max-w-xl mx-auto font-manrope"
          >
            Create your personalized landing page. Share all your links, showcase products, and track your audience—all in one beautiful profile.
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button 
              data-testid="get-started-btn"
              onClick={handleGetStarted}
              size="lg"
              className="rounded-full bg-violet-500 hover:bg-violet-600 text-white px-8 py-6 text-lg font-semibold glow-violet btn-glow"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              data-testid="see-example-btn"
              variant="outline"
              size="lg"
              onClick={() => navigate('/demo')}
              className="rounded-full bg-white/5 border-white/10 text-white hover:bg-white/10 px-8 py-6 text-lg"
            >
              See Example
            </Button>
          </motion.div>
        </div>

        {/* Phone Preview */}
        <motion.div 
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-20 max-w-xs mx-auto"
        >
          <div className="phone-frame p-4">
            <div className="h-full flex flex-col items-center pt-8 pb-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">JD</span>
              </div>
              <h3 className="mt-4 font-outfit font-bold text-white">@johndoe</h3>
              <p className="text-sm text-[#A1A1AA] mt-1">Creator & Designer</p>
              
              <div className="w-full mt-6 space-y-3 px-2">
                {['My Website', 'YouTube Channel', 'Latest Course'].map((text, i) => (
                  <div 
                    key={i}
                    className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-center text-sm text-white hover:bg-white/10 transition-all cursor-pointer"
                  >
                    {text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Features Bento Grid */}
        <section className="mt-32 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white">
              Everything You Need
            </h2>
            <p className="mt-4 text-[#A1A1AA]">
              Powerful features to grow your online presence
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bento-card p-6 ${feature.className}`}
              >
                <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-violet-400" />
                </div>
                <h3 className="font-outfit font-semibold text-lg text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-[#A1A1AA]">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <motion.section 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 max-w-3xl mx-auto text-center"
        >
          <div className="bento-card p-12 relative overflow-hidden">
            <div className="absolute inset-0 hero-glow opacity-50" />
            <div className="relative z-10">
              <Zap className="w-12 h-12 text-violet-400 mx-auto mb-6" />
              <h2 className="font-outfit font-bold text-3xl text-white mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-[#A1A1AA] mb-8">
                Join thousands of creators who trust One Link for their online presence.
              </p>
              <Button 
                data-testid="cta-btn"
                onClick={handleGetStarted}
                size="lg"
                className="rounded-full bg-violet-500 hover:bg-violet-600 text-white px-8 py-6 text-lg font-semibold glow-violet"
              >
                Create Your Link
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </motion.section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#27272A] py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-violet-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-outfit font-semibold text-white">One Link</span>
          </div>
          <p className="text-sm text-[#71717A]">
            © 2024 One Link. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
